import { DecimalPipe, NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { HeaderComponent } from "@components/header/header.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { NgbCollapse, NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import _ from "lodash";
import { Subject, combineLatest } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IconComponent } from "~app/common/components/icon/icon.component";
import { MessagePanelComponent } from "~app/common/components/message-panel/message-panel.component";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { SkogsvardAtgardsGrupp, SkogsvardAtgardsTyp, SkogsvardBetalningstyp, SkogsvardMattslagsTyper } from "~app/common/models/constants";
import { FeatureService } from "~app/common/services/feature.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { Avtal } from "~app/features/virkeskop/models/avtal-models";
import { Kontrakt, VacsKontraktstatus } from "~app/features/virkeskop/models/kontrakt-models";
import {
    IBetalningSkogsvardGrupp,
    IKontraktSkogsvardBetalningTableRow,
    KontraktSkogsvardBetalning,
    KontraktSkogsvardBetalningTableRow
} from "~app/features/virkeskop/models/skogsvard/skogsvard-models";
import { AvtalMediatorService } from "~app/features/virkeskop/services/avtal-mediator.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { NavigationService } from "~app/features/virkeskop/services/navigation.service";
import { SkogsvardBetalningMediatorService } from "~app/features/virkeskop/services/skogsvard-betalning-mediator.service";
import { SkogsvardService } from "~app/features/virkeskop/services/skogsvard.service";
import { canEdit } from "~app/role-permissions";
import { BetalningSkogsvardCommentComponent } from "./betalning-skogsvard-comment/betalning-skogsvard-comment.component";
interface ISkogsvardGruppInformation {
    atgarder?: Atgard[];
    betalningar?: IKontraktSkogsvardBetalningTableRow[];
    grupp: string;
}

@Component({
    selector: "rask-betalning-skogsvard",
    templateUrl: "./betalning-skogsvard.component.html",
    styleUrls: ["./betalning-skogsvard.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        NgbCollapse,
        HeaderComponent,
        RouterLink,
        ListComponent,
        ListColumnDirective,
        IconButtonComponent,
        NgbPopover,
        SimpleSelectorComponent,
        FormsModule,
        ButtonComponent,
        InputNumberComponent,
        DecimalPipe,
        IconComponent,
        MessagePanelComponent
    ]
})
export class BetalningSkogsvardComponent implements OnInit, OnDestroy {
    protected readonly fakturaInfoText = "Betalning för skogsvård faktureras efter utförd åtgärd";
    protected readonly livkidInfoText = "Skogsvården dras från självfakturerat belopp vid tillfället självfakturan ställs ut";
    protected readonly selectMattslag: string[] = [
        SkogsvardMattslagsTyper.ha,
        SkogsvardMattslagsTyper.timme
    ];

    protected allaSkogsvardBetalningar: KontraktSkogsvardBetalning[] = [];
    protected aprisEdited = false;
    protected beloppEdited = false;

    // Sorteringsordning
    protected betalningSkogsvardGrupp: IBetalningSkogsvardGrupp[] = [
        {
            sekvensnummer: 1,
            atgardsGrupp: SkogsvardAtgardsGrupp.rojning
        },
        {
            sekvensnummer: 2,
            atgardsGrupp: SkogsvardAtgardsGrupp.markberedning
        },
        {
            sekvensnummer: 3,
            atgardsGrupp: SkogsvardAtgardsGrupp.foryngring
        },
        {
            sekvensnummer: 4,
            atgardsGrupp: SkogsvardAtgardsGrupp.inventering
        },
        {
            sekvensnummer: 5,
            atgardsGrupp: SkogsvardAtgardsGrupp.ovrigt
        }
    ];
    protected betalningstypEdited = false;
    protected isCollapsed = true;
    protected isDirty = false;
    protected kontraktReadonly = false;
    protected kvantitetEdited = false;
    protected mattslagEdited = false;
    protected popoverModel: number;
    protected skogsvardsAtgarder: Atgard[] = [];
    protected skogsvardsGruppersInfo: ISkogsvardGruppInformation[] = [];
    protected virkeskopsAvtal: Avtal;
    protected virkeskopsKontrakt: Kontrakt;

    protected legacyLockedMessage: IMessageModel[] = [];

    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly busyService = inject(BusyService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly navigationService = inject(NavigationService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly raskModalService = inject(RaskModalService);
    private readonly skogsvardBetalningMediatorService = inject(SkogsvardBetalningMediatorService);
    private readonly skogsvardService = inject(SkogsvardService);
    private readonly toastService = inject(ToastService);
    private readonly featureService = inject(FeatureService);

    private readonly canEdit = canEdit();

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.virkeskopsKontrakt = this.kontraktMediatorService.kontrakt;
        this.virkeskopsAvtal = this.avtalMediatorService.avtal;
        this.legacyLockedMessage = this.getLegacyLockedMessage();

        this.kontraktMediatorService.isReadonly$
            .pipe(
                map(readonly => readonly || !this.canEdit()),
                takeUntil(this.ngUnsubscribe$))
            .subscribe(readonly => this.kontraktReadonly = readonly);

        const skogsvardList$ = this.kontraktMediatorService.atgarder$
            .pipe(
                map(atg => atg.filter(x => x.isSkogsvard))
            );

        combineLatest([
            skogsvardList$,
            this.skogsvardBetalningMediatorService.skogsvardBetalning$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([
                atgarder,
                skogsvardBetalningar
            ]) => {
                this.skogsvardsAtgarder = atgarder;
                this.allaSkogsvardBetalningar = skogsvardBetalningar;
                this.skogsvardsGruppersInfo = this.getSkogsvardGruppersInformation();
            });

        this.avtalMediatorService.dirty$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(dirty => {
                this.isDirty = dirty;
            });
    }

    protected aprisChanged($event): void {
        if ($event) {
            this.aprisEdited = true;
        }
    }

    protected beloppChanged($event) {
        if ($event) {
            this.beloppEdited = true;
        }
    }

    protected betalningstypChanged($event): void {
        if ($event) {
            this.betalningstypEdited = true;
        }
    }

    protected calculateBelopp(betalning?: KontraktSkogsvardBetalning): number {
        if (ObjectUtils.isNullOrUndefined(betalning?.kvantitet) ||
            ObjectUtils.isNullOrUndefined(betalning?.apris)) {
            return 0;
        }

        const belopp = betalning.kvantitet * betalning.apris;
        return belopp < 0 ? -_.round(-belopp) : _.round(belopp);
    }

    protected calculateSum(skogsvardBetalningar: KontraktSkogsvardBetalning[]): number {
        if (skogsvardBetalningar.length > 0) {
            const totalCalculated = skogsvardBetalningar.filter(x => !this.shouldHandleBelopp(x))
                .reduce((ty, u) => ty + (u.apris * u.kvantitet), 0);
            const totalBelopp = skogsvardBetalningar.filter(x => this.shouldHandleBelopp(x)).reduce((ty, u) => ty + u.belopp, 0);
            const total = totalCalculated + totalBelopp;
            return _.round(total, 0);
        }
        return 0;
    }

    protected cancel(popover: NgbPopover = null) {
        if (popover) {
            popover.close();
        }
    }

    protected editComment = (betalningId: number, editComment: string): void => {
        const betalning = this.allaSkogsvardBetalningar.find(x => x.id === betalningId);
        if (betalning) {
            betalning.anteckningIntern = editComment;
            this.editSkogsvardBetalning(betalning);
        }
    };

    protected formatDateMonthString(date: Date): string {
        return DateUtils.formatDateString(date, DateUtils.dateMonthformat);
    }

    protected formatDateString(date: Date): string {
        return DateUtils.formatDateString(date, DateUtils.dateformat);
    }

    protected getBetalningInfoText(betalningstyp: string): string {
        switch (betalningstyp) {
            case SkogsvardBetalningstyp.likvid:
                return this.livkidInfoText;
            case SkogsvardBetalningstyp.faktura:
                return this.fakturaInfoText;
            default:
                return "";
        }
    }

    protected getHeaderNamn(skogsvardGruppInfo: ISkogsvardGruppInformation) {
        if (skogsvardGruppInfo.grupp === SkogsvardAtgardsGrupp.foryngring) {
            const anyForyngring = skogsvardGruppInfo.atgarder.some(x => x.atgardsgrupp === SkogsvardAtgardsGrupp.foryngring);
            const anyPlantleverans = skogsvardGruppInfo.atgarder.some(x => x.atgardsgrupp === SkogsvardAtgardsGrupp.plantleverans);
            if (anyForyngring && anyPlantleverans) {
                return `${SkogsvardAtgardsGrupp.foryngring} & ${SkogsvardAtgardsGrupp.plantleverans}`;
            } else if (anyForyngring) {
                return SkogsvardAtgardsGrupp.foryngring;
            } else if (anyPlantleverans) {
                return SkogsvardAtgardsGrupp.plantleverans;
            }
        }
        return skogsvardGruppInfo.grupp;
    }

    protected getSkogsvardBetalningarForGrupp(grupp: string): IKontraktSkogsvardBetalningTableRow[] {
        const avslutatAvraknat = this.virkeskopsKontrakt.vacsKontraktstatus === VacsKontraktstatus.Avraknat ||
            this.virkeskopsKontrakt.vacsKontraktstatus === VacsKontraktstatus.Avslutat;

        const sortedList = this.getSortedList(grupp);
        const context = {
            skogsvardsAtgarder: this.skogsvardsAtgarder,
            allaBetalningar: this.allaSkogsvardBetalningar,
            firstVacsRowFound: false,
            firstFoundPerType: new Set<string>(),
            avslutatAvraknat,
            legacySkogsvardBetalraderLocked: this.virkeskopsKontrakt.legacySkogsvardBetalraderLocked
        };
        const resultList: IKontraktSkogsvardBetalningTableRow[] = [];

        sortedList.forEach(row => {
            const newRow = new KontraktSkogsvardBetalningTableRow(row, context);
            resultList.push(newRow);
        });
        return resultList;
    }

    protected getSkogsvarsdAtgarderForGrupp(grupp: string): Atgard[] {
        return this.skogsvardsAtgarder?.filter(x =>
            SkogsvardAtgardsGrupp.matchesGrupp(x.atgardsgrupp, grupp) && this.visibleInBetalning(x.atgardstyp, x.indirektKostnad));
    }

    protected getSkogvardsgrupper(): string {
        if (this.skogsvardsAtgarder.length > 0) {
            const grupper = this.skogsvardsAtgarder.filter(g => this.visibleInBetalning(g.atgardstyp, g.indirektKostnad))
                .map(x => x.atgardsgrupp);

            return _.uniq(grupper).join(", ");
        }
        return null;
    }

    protected getSkogvardstyper(): string {
        if (this.skogsvardsAtgarder.length > 0) {
            const typer = this.skogsvardsAtgarder.filter(g => this.visibleInBetalning(g.atgardstyp, g.indirektKostnad))
                .map(x => x.atgardstyp);
            return _.uniq(typer).join(", ");
        }
        return null;
    }

    protected kvantitetChanged($event): void {
        if ($event) {
            this.kvantitetEdited = true;
        }
    }

    protected mattslagChanged($event): void {
        if ($event) {
            this.mattslagEdited = true;
        }
    }

    protected navigateTo(atgardId: number, event: Event) {
        const avtalId = this.virkeskopsAvtal.id;
        const kontraktId = this.virkeskopsKontrakt.id;

        const {
            avtalId$: avtalId$, kontraktId$: kontraktId$, atgardId$: atgardId$
        } = this.navigationService;

        if (avtalId !== avtalId$.value
            || kontraktId !== kontraktId$.value
            || atgardId !== atgardId$.value
        ) {
            // om någon parameter har ändrats hanteras navigering av queryParams subscription i avtalskomponenten
            return;
        }

        event.preventDefault();
        avtalId$.next(avtalId);
        kontraktId$.next(kontraktId);
        atgardId$.next(atgardId);
    }

    protected openComment(rowId: number, comment: string): void {
        this.raskModalService.open("Ändra/lägg till anteckning", BetalningSkogsvardCommentComponent, "md", true, {
            betalningId: rowId,
            editComment: comment,
            commentUpdatedCallback: this.editComment,
            editMode: !this.kontraktReadonly
        });
    }

    protected saveApris(betalningId: number, apris: number, popover: NgbPopover = null): void {
        const betalning = this.allaSkogsvardBetalningar.find(x => x.id === betalningId);
        if (apris) {
            betalning.apris = apris;
            this.editSkogsvardBetalning(betalning);
            this.aprisEdited = false;
        }

        if (popover) {
            popover.close();
        }
    }

    protected saveBelopp(betalningId: number, belopp: number, popover: NgbPopover = null) {
        const betalning = this.allaSkogsvardBetalningar.find(x => x.id === betalningId);
        betalning.belopp = belopp;
        this.editSkogsvardBetalning(betalning);
        this.beloppEdited = false;

        if (popover) {
            popover.close();
        }
    }

    protected saveBetalningstyp(betalningId: number, betalningstyp: string, popover: NgbPopover = null): void {
        const betalning = this.allaSkogsvardBetalningar.find(x => x.id === betalningId);
        if (betalningstyp) {
            betalning.betalningstyp = betalningstyp;
            this.editSkogsvardBetalning(betalning);
            this.betalningstypEdited = false;
        }

        if (popover) {
            popover.close();
        }
    }

    protected saveKvantitet(betalningId: number, kvantitet: number, popover: NgbPopover = null) {
        const betalning = this.allaSkogsvardBetalningar.find(x => x.id === betalningId);
        betalning.kvantitet = kvantitet;
        this.editSkogsvardBetalning(betalning);
        this.kvantitetEdited = false;

        if (popover) {
            popover.close();
        }
    }

    protected saveMattslag(betalningId: number, mattslag: string, popover: NgbPopover = null): void {
        const betalning = this.allaSkogsvardBetalningar.find(x => x.id === betalningId);
        if (mattslag) {
            betalning.mattslag = mattslag;
            this.editSkogsvardBetalning(betalning);
            this.mattslagEdited = false;
        }

        if (popover) {
            popover.close();
        }
    }

    protected trackById(_index: number, item: Atgard) {
        return item.guid;
    }

    protected isLegacySkogsvard(): boolean {
        // Om det finns någon betalningsrad som inte har en kopplad åtgärd så är det en legacy skogsvård
        return this.allaSkogsvardBetalningar.some(x => !x.virkeskopsatgardId);
    }

    private getLegacyLockedMessage(): IMessageModel[] {
        if (!this.virkeskopsKontrakt.legacySkogsvardBetalraderLocked) {
            return [];
        }

        return [
            {
                message: "Betalningsraderna för skogsvård är låsta och kan endast ändras i Vacs.",
                severity: MessageSeverity.Warning,
                anchor: null
            }
        ];
    }

    private editSkogsvardBetalning(betalning: KontraktSkogsvardBetalning) {
        const updateBetalning$ = this.skogsvardBetalningMediatorService.updateSkogsvardBetalning(this.virkeskopsKontrakt.guid, betalning);

        this.busyService.addWork(updateBetalning$).subscribe(() => {
            this.toastService.add(`Betalningsrad uppdaterad för åtgärd: ${betalning.atgardstypVacs}`);
        });
    }

    private getDistinctAtgardsGrupper(skogsvardBetalningar: KontraktSkogsvardBetalning[]): IBetalningSkogsvardGrupp[] {
        // Hämta sekvensnummer och sortera
        if (skogsvardBetalningar.length > 0) {
            const grupper = skogsvardBetalningar.map(x => x.atgardsGrupp);
            const unikaGrupper = _.uniq<string>(grupper);
            return _.chain(this.betalningSkogsvardGrupp)
                .filter(x => unikaGrupper.includes(x.atgardsGrupp))
                .sortBy(x => x.sekvensnummer)
                .value();
        }
        return null;
    }

    private getSkogsvardGruppersInformation(): ISkogsvardGruppInformation[] {
        return this.getDistinctAtgardsGrupper(this.allaSkogsvardBetalningar)?.map(x => x.atgardsGrupp)
            .map(distinctGrupp => ({
                grupp: distinctGrupp,
                atgarder: this.getSkogsvarsdAtgarderForGrupp(distinctGrupp),
                betalningar: this.getSkogsvardBetalningarForGrupp(distinctGrupp)
            })) ?? [];
    }

    private getSortedList(grupp: string) {
        const list = this.allaSkogsvardBetalningar
            .filter(a => SkogsvardAtgardsGrupp.matchesGrupp(a.atgardsGrupp, grupp));

        return _.orderBy(list, [
            (x) => x.virkeskopsatgardId,
            (x) => x.skickadTillVacs,
            (x) => x.apris < 0,
            (x) => x.datumTillagd,
            (x) => x.atgardstypVacs
        ], [
            "asc",
            "desc",
            "asc",
            "desc",
            "asc"
        ]);
    }

    private shouldHandleBelopp(betalning: KontraktSkogsvardBetalning): boolean {
        return this.skogsvardService.shouldHandleBelopp(betalning);
    }

    private visibleInBetalning(atgardstyp: string, indirektKostnad: boolean): boolean {
        if (this.virkeskopsKontrakt.isSkogsvardsKontrakt) {
            return true;
        }

        return atgardstyp !== SkogsvardAtgardsTyp.nsSiktrojning && atgardstyp !== SkogsvardAtgardsTyp.siktrojning || !indirektKostnad;
    }
}
